import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import {
  AdminTable, AdminSectionHeader, SectionsEditor, StatusBadge,
  inputCls, labelCls, tdCls, exportCsv, InlineError,
  type SectionDraft,
} from '@/components/admin/AdminShared';

type NamedRow = { id: number; name: string };

// Course metadata: instructor, majors, intro video, Featured. Shared by the
// create form and the edit modal.
type CourseMetaValue = {
  instructorId:  string;     // '' = none
  categoryIds:   number[];   // a course can be in MANY majors
  isFeatured:    boolean;
  introVideoUrl: string;
};

function CourseMeta({
  instructors, categories, value, onChange, isAdmin,
}: {
  instructors: NamedRow[];
  categories:  NamedRow[];
  value:       CourseMetaValue;
  onChange:    (patch: Partial<CourseMetaValue>) => void;
  /** Only Level-1 admins can pick the instructor or toggle Featured. */
  isAdmin:     boolean;
}) {
  const toggleMajor = (id: number) => {
    const set = new Set(value.categoryIds);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange({ categoryIds: [...set] });
  };
  return (
    <>
      {isAdmin && (
        <div>
          <label className={labelCls}>Instructor</label>
          <select value={value.instructorId}
                  onChange={e => onChange({ instructorId: e.target.value })}
                  className={inputCls}>
            <option value="">— None —</option>
            {instructors.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
          </select>
        </div>
      )}

      <div>
        <label className={labelCls}>Majors — pick one or more</label>
        {categories.length === 0 ? (
          <p className="text-[#52525B] text-xs">No majors yet. Add some in the Majors tab.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map(c => {
              const on = value.categoryIds.includes(c.id);
              return (
                <button type="button" key={c.id} onClick={() => toggleMajor(c.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                                    ${on
                                      ? 'bg-yellow-400 text-black border-yellow-400'
                                      : 'text-[#A1A1AA] border-white/10 hover:border-white/30'}`}>
                  {on ? '✓ ' : ''}{c.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>Intro / preview video — free for everyone</label>
        <input type="text" inputMode="url" value={value.introVideoUrl}
               onChange={e => onChange({ introVideoUrl: e.target.value })}
               placeholder="R2 file name (e.g. intro.mp4) or a YouTube/Vimeo/MP4 link"
               className={inputCls} />
        <p className="text-[10px] text-[#52525B] mt-1">
          Plays for all visitors, even before they buy. Leave blank for none.
        </p>
      </div>

      {isAdmin && (
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={value.isFeatured}
                 onChange={e => onChange({ isFeatured: e.target.checked })}
                 className="w-4 h-4 accent-yellow-400" />
          <span className="text-sm text-[#E4E4E7]">
            Featured — show in the homepage "popular" row
          </span>
        </label>
      )}
    </>
  );
}

// ─── Edit modal ───────────────────────────────────────────────────────────────
function EditModal({
  course,
  instructors,
  categories,
  isAdmin,
  onClose,
  onSaved,
}: {
  course: {
    id: number; title: string; description: string | null; price: string;
    imageUrl: string | null;
    instructorId: number | null; categoryIds: number[];
    isFeatured: boolean; introVideoUrl: string | null;
    sections: SectionDraft[];
  };
  instructors: NamedRow[];
  categories:  NamedRow[];
  isAdmin:     boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const utils = trpc.useUtils();
  const [form, setForm] = useState({
    title:         course.title,
    description:   course.description ?? '',
    price:         course.price,
    imageUrl:      course.imageUrl ?? '',
    instructorId:  course.instructorId != null ? String(course.instructorId) : '',
    categoryIds:   course.categoryIds,
    isFeatured:    course.isFeatured,
    introVideoUrl: course.introVideoUrl ?? '',
    sections:      course.sections,
  });

  const updateCourse = trpc.courses.update.useMutation({
    onSuccess: async () => {
      // Refresh the admin management list and the public catalog
      await Promise.all([
        utils.courses.listMine.invalidate(),
        utils.courses.list.invalidate(),
      ]);
      onSaved();
      onClose();
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Defensive parse — NaN would silently fail server validation
    const priceNum = parseFloat(form.price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      // The InlineError below will pick up the manual error via mutation reset
      updateCourse.reset();
      alert('Enter a valid price (0 or greater).');
      return;
    }
    updateCourse.mutate({
      id:            course.id,
      title:         form.title,
      description:   form.description || undefined,
      price:         priceNum,
      imageUrl:      form.imageUrl.trim() || null,
      instructorId:  form.instructorId ? Number(form.instructorId) : null,
      categoryIds:   form.categoryIds,
      isFeatured:    form.isFeatured,
      introVideoUrl: form.introVideoUrl.trim() || null,
      sections:      form.sections
        .filter(s => s.name.trim())
        .map(s => ({ ...s, videoUrl: s.videoUrl?.trim() || null })),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50
                 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#09090B] border border-[#27272A] rounded-3xl
                      w-full max-w-xl max-h-[90vh] overflow-y-auto scrollbar-hide">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-black text-white">Edit Course</h3>
            <button onClick={onClose}
                    className="text-[#52525B] hover:text-white text-xl leading-none">✕</button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className={labelCls}>Title</label>
              <input value={form.title} required
                     onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                     className={inputCls} placeholder="Course title" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} rows={3}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        className={`${inputCls} resize-none`} placeholder="Optional description" />
            </div>
            <div>
              <label className={labelCls}>Price (KD)</label>
              <input type="number" min={0} step={0.01} value={form.price} required
                     onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                     className={inputCls} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>Cover image URL</label>
              <input type="url" value={form.imageUrl}
                     onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                     className={inputCls} placeholder="https://… (paste an image link)" />
              {form.imageUrl && (
                <img src={form.imageUrl} alt="" className="mt-2 max-h-32 rounded-xl border border-white/10" />
              )}
            </div>

            <CourseMeta
              instructors={instructors}
              categories={categories}
              isAdmin={isAdmin}
              value={{
                instructorId:  form.instructorId,
                categoryIds:   form.categoryIds,
                isFeatured:    form.isFeatured,
                introVideoUrl: form.introVideoUrl,
              }}
              onChange={patch => setForm(f => ({ ...f, ...patch }))}
            />

            <div className="pt-2">
              <SectionsEditor
                sections={form.sections}
                onChange={sections => setForm(f => ({ ...f, sections }))}
              />
            </div>

            {updateCourse.error && (
              <InlineError message={updateCourse.error.message} />
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                      className="flex-1 py-3 border border-white/10 text-[#A1A1AA]
                                 hover:text-white rounded-2xl text-sm font-medium transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={updateCourse.isPending}
                      className="flex-1 py-3 bg-yellow-400 hover:bg-yellow-400
                                 text-black font-black rounded-2xl text-sm transition-colors
                                 disabled:opacity-50">
                {updateCourse.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AdminCourses() {
  const utils = trpc.useUtils();
  const { isAdmin } = useAuth();
  // listMine: admins see all courses, instructors see only their own.
  const { data: courses = [], refetch } = trpc.courses.listMine.useQuery();
  const { data: purchases = [] }        = trpc.purchases.list.useQuery(undefined, { enabled: isAdmin });
  const { data: instructors = [] }      = trpc.instructors.list.useQuery();
  const { data: categories = [] }       = trpc.categories.list.useQuery();

  /** Refresh the admin table AND the public catalog cache. */
  const refreshAll = async () => {
    await Promise.all([
      utils.courses.listMine.invalidate(),
      utils.courses.list.invalidate(),
      refetch(),
    ]);
  };

  const deleteCourse = trpc.courses.delete.useMutation({ onSuccess: () => refreshAll() });
  const toggleHidden = trpc.courses.update.useMutation({ onSuccess: () => refreshAll() });
  const reorder      = trpc.courses.reorder.useMutation({ onSuccess: () => refreshAll() });
  const createCourse = trpc.courses.create.useMutation({
    onSuccess: async () => {
      await refreshAll();
      setForm({
        title: '', description: '', price: '', imageUrl: '',
        instructorId: '', categoryIds: [], isFeatured: false, introVideoUrl: '',
        sections: [{ name: '', durationMinutes: 30, order: 0, videoUrl: '' }],
      });
      setShowAdd(false);
    },
  });

  const [editTarget, setEditTarget] = useState<(typeof courses)[number] | null>(null);
  const [deleteId,   setDeleteId]   = useState<number | null>(null);
  const [showAdd,    setShowAdd]    = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', price: '', imageUrl: '',
    instructorId: '', categoryIds: [] as number[], isFeatured: false, introVideoUrl: '',
    sections: [{ name: '', durationMinutes: 30, order: 0, videoUrl: '' }] as SectionDraft[],
  });

  /** All major ids a course belongs to (via the join relation, with fallback). */
  const courseMajorIds = (c: any): number[] => {
    const fromJoin = (c.courseCategories ?? []).map((cc: any) => cc.categoryId);
    if (fromJoin.length) return fromJoin;
    return c.categoryId != null ? [c.categoryId] : [];
  };

  // ── Filters ────────────────────────────────────────────────────────────
  const [fInstructor, setFInstructor] = useState('');
  const [fMajor,      setFMajor]      = useState('');
  const [fMinPrice,   setFMinPrice]   = useState('');
  const [fMaxPrice,   setFMaxPrice]   = useState('');
  const [fMinPurch,   setFMinPurch]   = useState('');

  const purchaseCount = (id: number) => purchases.filter(p => p.courseId === id).length;
  const filteredCourses = courses.filter(c => {
    if (fInstructor && String((c as any).instructorId ?? '') !== fInstructor) return false;
    if (fMajor && !courseMajorIds(c).map(String).includes(fMajor)) return false;
    const price = parseFloat(c.price as string) || 0;
    if (fMinPrice && price < Number(fMinPrice)) return false;
    if (fMaxPrice && price > Number(fMaxPrice)) return false;
    if (isAdmin && fMinPurch && purchaseCount(c.id) < Number(fMinPurch)) return false;
    return true;
  });
  const filtersActive = !!(fInstructor || fMajor || fMinPrice || fMaxPrice || fMinPurch);

  // Reorder is only meaningful on the full, unfiltered list.
  const canReorder = isAdmin && !filtersActive;
  const moveCourse = (id: number, dir: -1 | 1) => {
    const ids = courses.map(c => c.id);
    const idx = ids.indexOf(id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    reorder.mutate({ orderedIds: ids });
  };

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const priceNum = parseFloat(form.price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      alert('Enter a valid price (0 or greater).');
      return;
    }
    createCourse.mutate({
      title:         form.title,
      description:   form.description || undefined,
      price:         priceNum,
      imageUrl:      form.imageUrl.trim() || null,
      instructorId:  form.instructorId ? Number(form.instructorId) : null,
      categoryIds:   form.categoryIds,
      isFeatured:    form.isFeatured,
      introVideoUrl: form.introVideoUrl.trim() || null,
      sections:      form.sections
        .filter(s => s.name.trim())
        .map(s => ({ ...s, videoUrl: s.videoUrl?.trim() || null })),
    });
  };

  const handleExport = () =>
    exportCsv(
      courses.map(c => ({
        ID:          c.id,
        Title:       c.title,
        Description: c.description ?? '',
        Price_KD:    c.price,
        Sections:    c.sections.length,
        Purchases:   purchases.filter(p => p.courseId === c.id).length,
        Created:     new Date(c.createdAt).toLocaleDateString(),
      })),
      'mrpanadol_courses'
    );

  return (
    <div>
      <AdminSectionHeader
        title="Courses"
        count={courses.length}
        onExport={handleExport}
      />

      {/* ── Add course form toggle ── */}
      <div className="mb-6">
        <button
          onClick={() => setShowAdd(v => !v)}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-400
                     text-black font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {showAdd ? 'Cancel' : 'Add Course'}
        </button>
      </div>

      {/* ── Add course form ── */}
      {showAdd && (
        <div className="bg-[#09090B] border border-yellow-400/20 border-dashed
                        rounded-3xl p-6 mb-8">
          <h3 className="text-base font-black text-white mb-5">New Course</h3>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Title *</label>
                <input value={form.title} required
                       onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                       placeholder="e.g. Thermodynamics" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Price (KD) *</label>
                <input type="number" min={0} step={0.01} value={form.price} required
                       onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                       placeholder="0 = free" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Description</label>
                <textarea rows={2} value={form.description}
                          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                          placeholder="Optional course description"
                          className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Cover image URL</label>
                <input type="url" value={form.imageUrl}
                       onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                       placeholder="https://… (paste an image link)"
                       className={inputCls} />
                {form.imageUrl && (
                  <img src={form.imageUrl} alt="" className="mt-2 max-h-32 rounded-xl border border-white/10" />
                )}
              </div>
            </div>

            <CourseMeta
              instructors={instructors}
              categories={categories}
              isAdmin={isAdmin}
              value={{
                instructorId:  form.instructorId,
                categoryIds:   form.categoryIds,
                isFeatured:    form.isFeatured,
                introVideoUrl: form.introVideoUrl,
              }}
              onChange={patch => setForm(f => ({ ...f, ...patch }))}
            />

            <SectionsEditor
              sections={form.sections}
              onChange={sections => setForm(f => ({ ...f, sections }))}
            />

            {createCourse.error && <InlineError message={createCourse.error.message} />}

            <button type="submit" disabled={createCourse.isPending}
                    className="bg-yellow-400 hover:bg-yellow-400 text-black font-black
                               px-6 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-50">
              {createCourse.isPending ? 'Creating…' : 'Create Course'}
            </button>
          </form>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        {isAdmin && instructors.length > 0 && (
          <div>
            <label className={labelCls}>Instructor</label>
            <select value={fInstructor} onChange={e => setFInstructor(e.target.value)} className={inputCls}>
              <option value="">All</option>
              {instructors.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
            </select>
          </div>
        )}
        {categories.length > 0 && (
          <div>
            <label className={labelCls}>Major</label>
            <select value={fMajor} onChange={e => setFMajor(e.target.value)} className={inputCls}>
              <option value="">All</option>
              {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>Min price</label>
          <input type="number" min={0} value={fMinPrice} onChange={e => setFMinPrice(e.target.value)}
                 placeholder="any" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Max price</label>
          <input type="number" min={0} value={fMaxPrice} onChange={e => setFMaxPrice(e.target.value)}
                 placeholder="any" className={inputCls} />
        </div>
        {isAdmin && (
          <div>
            <label className={labelCls}>Min purchases</label>
            <input type="number" min={0} value={fMinPurch} onChange={e => setFMinPurch(e.target.value)}
                   placeholder="any" className={inputCls} />
          </div>
        )}
        {filtersActive && (
          <button
            onClick={() => { setFInstructor(''); setFMajor(''); setFMinPrice(''); setFMaxPrice(''); setFMinPurch(''); }}
            className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                       hover:border-white/20 px-3 py-2 rounded-lg transition-colors">
            Clear filters
          </button>
        )}
      </div>

      {/* ── Courses table ── */}
      <AdminTable
        headers={[...(canReorder ? ['Order'] : []), 'Title', 'Majors', 'Price', 'Sections', 'Purchases', 'Created', 'Actions']}
        empty={filteredCourses.length === 0}
      >
        {filteredCourses.map((c, idx) => (
          <tr key={c.id} className="border-t border-white/[0.04] group">
            {canReorder && (
              <td className={tdCls}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveCourse(c.id, -1)} disabled={idx === 0 || reorder.isPending}
                          title="Move up"
                          className="w-6 h-5 flex items-center justify-center rounded text-[#A1A1AA]
                                     hover:text-white hover:bg-white/10 disabled:opacity-25 transition-colors">▲</button>
                  <button onClick={() => moveCourse(c.id, 1)} disabled={idx === filteredCourses.length - 1 || reorder.isPending}
                          title="Move down"
                          className="w-6 h-5 flex items-center justify-center rounded text-[#A1A1AA]
                                     hover:text-white hover:bg-white/10 disabled:opacity-25 transition-colors">▼</button>
                </div>
              </td>
            )}
            <td className={tdCls}>
              <div className="flex items-center gap-2">
                <p className="text-white font-semibold text-sm">{c.title}</p>
                {(c as any).isHidden && (
                  <span className="bg-[#27272A] text-[#A1A1AA] text-[10px] font-bold
                                   px-2 py-0.5 rounded-full border border-white/10">
                    Hidden
                  </span>
                )}
              </div>
              {c.description && (
                <p className="text-[#52525B] text-xs mt-0.5 max-w-[240px] truncate">
                  {c.description}
                </p>
              )}
            </td>
            <td className={tdCls}>
              <div className="flex flex-wrap gap-1 max-w-[200px]">
                {courseMajorIds(c).length === 0
                  ? <span className="text-[#3F3F46] text-xs">—</span>
                  : courseMajorIds(c).map((mid: number) => {
                      const name = categories.find(cat => cat.id === mid)?.name;
                      if (!name) return null;
                      return (
                        <span key={mid} className="text-[10px] text-[#A1A1AA] bg-[#18181B]
                                                    border border-white/10 px-2 py-0.5 rounded-full">
                          {name}
                        </span>
                      );
                    })}
              </div>
            </td>
            <td className={`${tdCls} text-yellow-400 font-black`}>
              {parseFloat(c.price as string) === 0 ? 'Free' : `${c.price} KD`}
            </td>
            <td className={`${tdCls} text-[#A1A1AA]`}>{c.sections.length}</td>
            <td className={`${tdCls} text-[#A1A1AA]`}>
              {isAdmin ? purchases.filter(p => p.courseId === c.id).length : '—'}
            </td>
            <td className={`${tdCls} text-[#52525B] text-xs whitespace-nowrap`}>
              {new Date(c.createdAt).toLocaleDateString()}
            </td>
            <td className={`${tdCls}`}>
              <div className="flex gap-2 items-center flex-wrap">
                {/* Edit button */}
                <button
                  onClick={() => setEditTarget(c as any)}
                  className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                             hover:border-white/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Edit
                </button>

                {/* Hide / Show toggle */}
                <button
                  onClick={() => toggleHidden.mutate({ id: c.id, isHidden: !(c as any).isHidden })}
                  disabled={toggleHidden.isPending}
                  title={(c as any).isHidden
                    ? 'Make this course visible in the public catalog again'
                    : 'Hide from the public catalog — existing buyers keep access'}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors
                              disabled:opacity-50
                              ${(c as any).isHidden
                                ? 'text-yellow-400 border-yellow-400/30 hover:border-yellow-400/60'
                                : 'text-[#A1A1AA] border-white/10 hover:border-white/20 hover:text-white'}`}
                >
                  {(c as any).isHidden ? 'Show' : 'Hide'}
                </button>

                {/* Delete button */}
                {deleteId === c.id ? (
                  <div className="flex gap-1.5 items-center">
                    <span className="text-[#71717A] text-xs">Sure?</span>
                    <button
                      onClick={() => { deleteCourse.mutate({ id: c.id }); setDeleteId(null); }}
                      className="text-xs text-red-400 border border-red-400/30 px-2.5 py-1
                                 rounded-lg hover:bg-red-400/10 transition-colors"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setDeleteId(null)}
                      className="text-xs text-[#71717A] border border-white/10 px-2.5 py-1
                                 rounded-lg hover:text-white transition-colors"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteId(c.id)}
                    className="text-xs text-red-400 border border-red-400/20
                               hover:border-red-400/40 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>

      {/* ── Edit modal ── */}
      {editTarget && (
        <EditModal
          instructors={instructors}
          categories={categories}
          isAdmin={isAdmin}
          course={{
            id:            editTarget.id,
            title:         editTarget.title,
            description:   editTarget.description ?? null,
            price:         String(editTarget.price),
            imageUrl:      (editTarget as any).imageUrl ?? null,
            instructorId:  (editTarget as any).instructorId ?? null,
            categoryIds:   courseMajorIds(editTarget),
            isFeatured:    Boolean((editTarget as any).isFeatured),
            introVideoUrl: (editTarget as any).introVideoUrl ?? null,
            sections:      (editTarget.sections ?? []).map(s => ({
              name:            s.name,
              durationMinutes: s.durationMinutes,
              order:           s.order,
              videoUrl:        (s as any).videoUrl ?? '',
            })),
          }}
          onClose={() => setEditTarget(null)}
          onSaved={() => refetch()}
        />
      )}
    </div>
  );
}
